/** @format */

import {ChangeEvent, useEffect, useState} from 'react';

import Alert from '@mui/material/Alert';
import {Box, CircularProgress, Grid, IconButton, LinearProgress, Typography} from '@mui/material';
import {Trans, useTranslation} from 'react-i18next';
import {makeStyles} from '@mui/styles';
import {useNavigate, useLocation} from 'react-router-dom';
import CreateOutlinedIcon from '@mui/icons-material/CreateOutlined';
import DeleteForeverOutlinedIcon from '@mui/icons-material/DeleteForeverOutlined';
import RecordVoiceOverOutlinedIcon from '@mui/icons-material/RecordVoiceOverOutlined';

import Breadcrumbs from 'components/Breadcrumbs';
import ConnectRows from 'components/connect/ConnectRows';
import InviteGuest from 'pages/Wizard/Connect/InviteGuest';
import LegacyTextField from 'components/inputs/LegacyTextField';
import PrimaryMediumButton from 'components/buttons/PrimaryMediumButton';
import TransparentSmallButton from 'components/buttons/TransparentSmallButton';
import {IsRuVersion, useCallbackOnReturn, useSearchParams} from 'helpers/routerHelpers';
import {useMixpanelContext} from 'contexts/MixpanelContext';

import {YalperPlatform} from 'graphql/generated/types';

import {ALL_PLATFORMS, rejectRuPlatforms} from 'helpers/adTypeHelpers';

import {useGetCurrentUserCompanyQuery, useUpdateCompanyMutation} from '../graphql/__generated__';

const useStyles = makeStyles(theme => ({
  link: {
    fontSize: '16px',
    fontWeight: 400,
    color: theme.palette.figma_gray_light_1?.main,
  },
  companyTitle: {
    fontSize: '24px',
    fontWeight: 700,
    lineHeight: 'normal',
  },
  companyButton: {
    width: '48px',
    height: '48px',
    marginRight: '16px',
    borderRadius: '8px',
    background: '#202020',
    '&:last-of-type': {
      marginRight: '0',
    },
  },
  companyTitleInput: {
    width: '100%',
  },
  saveCompanyTitleButton: {
    height: '56px',
  },
  row: {
    padding: '16px',
    borderRadius: '8px',
    background: '#202020',
  },
  inviteGuestTitle: {
    fontSize: '16px',
    fontWeight: 700,
    letterSpacing: '0.131px',
  },
  inviteGuestDescription: {
    color: '#8B8B8B',
    fontSize: '14px',
    fontWeight: 400,
    letterSpacing: '0.131px',
  },
  inviteGuestButton: {
    display: 'flex',
    alignItems: 'center',
    color: '#F1F1F1',
    fontSize: '14px',
    fontWeight: 400,
    [theme.breakpoints.down('md')]: {
      width: '100%',
      marginTop: '16px',
    },
  },
  inviteGuestIcon: {
    marginRight: '8px',
  },
}));

export const Company = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const {t} = useTranslation();

  const searchParams = useSearchParams();
  const companyId = searchParams.get('companyId');
  const {mixpanel} = useMixpanelContext();

  const classes = useStyles();

  const [state, setState] = useState({name: ''});
  const [editMode, setEditMode] = useState(false);
  const [saveCompanyError, setSaveCompanyError] = useState('');
  const [showInviteGuest, setShowInviteGuest] = useState(false);

  const {loading, error, data, refetch} = useGetCurrentUserCompanyQuery({
    skip: !companyId,
    variables: {companyId},
  });
  const [updateCompany, {loading: updateProcessing}] = useUpdateCompanyMutation();

  useCallbackOnReturn(refetch);

  useEffect(() => {
    if (!data?.currentUser?.company) return;
    const {company} = data.currentUser;
    setState({name: company.name});
  }, [data]);

  if (error) {
    return <Alert severity='error'>{error.message}</Alert>;
  }
  if (loading || !data?.currentUser?.company) {
    return <LinearProgress style={{flex: 1}} />;
  }

  const breadcrumbs = [
    {
      href: '/home/settings',
      title: <Trans>Настройки</Trans>,
    },
    {
      href: '/home/settings/connect',
      title: <Trans>Проекты</Trans>,
    },
    {
      href: '',
      title: <Trans>Управление</Trans>,
    },
  ];

  const {type: userType, isNew, isSubscriptionLimitExceeded, company, roles} = data.currentUser;
  const platforms = (): YalperPlatform[] => {
    if (IsRuVersion()) {
      return ALL_PLATFORMS;
    }
    return rejectRuPlatforms(ALL_PLATFORMS);
  };

  const renderForm = () => {
    const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
      const {name, value} = event.target;
      setState({...state, [name]: value});
    };

    return (
      <LegacyTextField
        className={classes.companyTitleInput}
        error={Boolean(saveCompanyError)}
        placeholder={t('Название проекта', 'Название проекта')}
        value={state.name}
        inputProps={{name: 'name'}}
        myProps={{placeholderFocused: ''}}
        onChange={handleChange}
      />
    );
  };

  const handleSave = async () => {
    try {
      mixpanel.track('click_settings_save_company_button', {
        company_id: company.id,
        company_uuid: company.uuid,
        company_name: company.name,
      });

      await updateCompany({
        variables: {id: company.id, name: state.name},
      });

      setEditMode(false);
    } catch (e) {
      if (e instanceof Error) {
        setSaveCompanyError(e.message);
      }
    }
  };

  const handleEdit = () => {
    mixpanel.track('click_settings_edit_company_button', {
      company_id: company.id,
      company_uuid: company.uuid,
      company_name: company.name,
    });

    setEditMode(true);
  };

  const renderInviteGuest = () => {
    const handleOpen = () => {
      setShowInviteGuest(true);
    };

    const handleClose = () => {
      setShowInviteGuest(false);
    };

    return (
      <Box
        className={classes.row}
        mt={6}
        display={'flex'}
        flexWrap={'wrap'}
        alignItems={'center'}
        justifyContent={'space-between'}
      >
        <Box>
          <Typography className={classes.inviteGuestTitle}>
            <Trans>Если у вас нет доступов от кабинетов</Trans>
          </Typography>

          <Box mt={2}>
            <Typography className={classes.inviteGuestDescription}>
              <Trans>Отправьте ссылку своему маркетологу</Trans>
            </Typography>
          </Box>
        </Box>

        <PrimaryMediumButton className={classes.inviteGuestButton} onClick={handleOpen}>
          <RecordVoiceOverOutlinedIcon className={classes.inviteGuestIcon} fontSize='small' />

          <Trans>Попросить специалиста выдать доступы</Trans>
        </PrimaryMediumButton>

        {showInviteGuest && <InviteGuest onClose={handleClose} />}
      </Box>
    );
  };

  const handleDelete = async () => {
    mixpanel.track('click_settings_delete_company_button', {
      company_id: company.id,
      company_uuid: company.uuid,
      company_name: company.name,
    });

    navigate(`/home/settings/connect/company/confirm-delete-company?companyId=${company.id}`, {
      state: {background: location},
    });
  };

  return (
    <Box>
      <TransparentSmallButton
        className={classes.saveCompanyTitleButton}
        disabled={updateProcessing}
        onClick={handleSave}
      >
        <Typography className={classes.link}>
          <Trans>Сохранить</Trans>
        </Typography>
      </TransparentSmallButton>
      <Box>
        <Box mb={10}>
          <Breadcrumbs items={breadcrumbs} />
        </Box>

        {editMode ? (
          <Grid container spacing={4}>
            <Grid item xs={12} md={10}>
              {renderForm()}
            </Grid>
            <Grid container item alignItems='center' justifyContent='flex-end' xs={12} md={2}>
              {!updateProcessing && (
                <TransparentSmallButton
                  className={classes.saveCompanyTitleButton}
                  disabled={updateProcessing}
                  onClick={handleSave}
                >
                  <Typography className={classes.link}>
                    <Trans>Сохранить</Trans>
                  </Typography>
                </TransparentSmallButton>
              )}
              {updateProcessing && <CircularProgress />}
            </Grid>
          </Grid>
        ) : (
          <Box display='flex' justifyContent='space-between' alignItems={'center'}>
            <Typography className={classes.companyTitle}>{state.name}</Typography>

            <Box>
              <IconButton
                className={classes.companyButton}
                aria-label='company-edit-button'
                onClick={handleEdit}
                size='large'
              >
                <CreateOutlinedIcon htmlColor='#fff' />
              </IconButton>
              <IconButton
                className={classes.companyButton}
                aria-label='company-delete-button'
                onClick={handleDelete}
                size='large'
              >
                <DeleteForeverOutlinedIcon htmlColor='#fff' />
              </IconButton>
            </Box>
          </Box>
        )}

        {renderInviteGuest()}

        <Box mt={6}>
          <ConnectRows
            canEditLimits
            canSelectSubaccounts
            companyId={company.id}
            needPay={!isNew && userType === 'BUSINESS' && isSubscriptionLimitExceeded}
            tokens={company.tokens}
            platforms={platforms()}
            showMetaExtremismCaption={IsRuVersion()}
            userRole={roles}
          />
        </Box>
      </Box>
    </Box>
  );
};
